import { jsx } from "react/jsx-runtime";
import classNames from "classnames";
import { memo, useRef, useCallback, useMemo } from "react";
import { DepthContext, FocusableContext } from "./Contexts.js";
import { MenuItem } from "./MenuItem.js";
import { ActiveMenuItemProvider } from "./useActiveMenuItem.js";
import { MenuIdProvider } from "./useMenuId.js";
import { MouseMoveProvider } from "../../auxiliary/useMouseMove.js";
import { useComponentClassName } from "../../auxiliary/useComponentClassName.js";
import { toViewClass } from "../../utils/toViewClass.js";
function getFocusableItems(parent) {
  return Array.from(parent.querySelectorAll("li")).filter((node) => node.tabIndex >= -1);
}
function getClosestFocusable(parent, offset) {
  if (!document.activeElement) {
    return null;
  }
  if (!(document.activeElement instanceof HTMLLIElement)) {
    return null;
  }
  const list = getFocusableItems(parent);
  const currentlyFocusedIndex = list.indexOf(document.activeElement);
  return list[currentlyFocusedIndex + offset] ?? null;
}
const MenuInternal = memo((props) => {
  const menuRef = useRef(null);
  const componentClassName = useComponentClassName("menu");
  const nextFocusable = useCallback(() => {
    if (!menuRef.current) {
      return null;
    }
    return getClosestFocusable(menuRef.current, 1);
  }, []);
  const previousFocusable = useCallback(() => {
    if (!menuRef.current) {
      return null;
    }
    return getClosestFocusable(menuRef.current, -1);
  }, []);
  const menuId = props.id ?? "unknown";
  return /* @__PURE__ */ jsx(DepthContext.Provider, { value: 0, children: /* @__PURE__ */ jsx(MenuIdProvider, { menuId, children: /* @__PURE__ */ jsx(MouseMoveProvider, { elementRef: menuRef, children: /* @__PURE__ */ jsx(ActiveMenuItemProvider, { menuRef, children: /* @__PURE__ */ jsx("section", { className: classNames(
    componentClassName,
    toViewClass("showCaret", props.showCaret ?? true)
  ), children: /* @__PURE__ */ jsx("ul", { ref: menuRef, className: classNames(
    `${componentClassName}-list`,
    "is-expanded"
  ), children: /* @__PURE__ */ jsx(FocusableContext.Provider, { value: useMemo(() => ({
    nextFocusable,
    previousFocusable
  }), [nextFocusable, previousFocusable]), children: props.children }) }) }) }) }) }) });
});
const Menu = MenuInternal;
Menu.Item = MenuItem;
export {
  Menu
};
//# sourceMappingURL=Menu.js.map
